import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Input,
    Stack,
    Textarea,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { ROUTES } from "constants/routes-path";
import { checkErrors, hasError } from "utils/forms-utils";
import { trafficViolationService } from "services/trafficViolationService";
import { documentsTrafficViolationsService, trafficViolationDocumentTypes } from "services/documentsTrafficViolationsService";
import { FileAttachmentMultipleInput } from "components/FileAttachmentInput/FileAttachmentMultipleInput";
import TrafficViolationDocumentsListPageCardHeader from "./TrafficViolationDocumentsListPageCardHeader";
import { parseDateBrToISODateString } from "utils/utils";
import { isoToBrFormat } from "utils/utils";
import { documentsService } from "services/documentsService";
import { downloadFile, printFile } from "../../Documents/DocumentUtils";
import moment from "moment";
import "moment/locale/pt-br";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

function TrafficViolationDocumentsNew() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [urlSearchParams] = useSearchParams();
    const id = urlSearchParams.get("id");
    const parentId = urlSearchParams.get("parent_id");
    const showToast = useCustomToast();
    const attachmentRef = useRef(undefined);

    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");
    const [trafficViolationData, setTrafficViolationData] = useState(undefined);
    const [formData, setFormData] = useState(undefined);
    const parentType = 'documento-infracoes';
    const [fileAttachmentName, setAttachmentFileName] = useState(null);
    const [inputError, setInputError] = useState({});

    const ids = useMemo(() => ({
        documentType: "type",
        documentDate: "documentData",
        documentExpirationDate: "expirationDate",
        documentObservations: "observations",
        documentFiles: "files",
        documentFileInfo: "fileInfo",
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (data) => {
        return {
            [ids.documentType]: {
                value: trafficViolationDocumentTypes.find(
                    (opt) => opt.label === data?.tipo || opt.value === data?.tipo
                )?.value || "",
                validations: [{ required: true }],
            },
            [ids.documentDate]: {
                value: id
                    ? parseDateBrToISODateString(data?.data_documento)
                    : new Date().toISOString().split("T")[0],
                validations: [{ required: true }],
            },
            [ids.documentObservations]: {
                value: data?.observacoes || "",
                validations: [],
            },
            [ids.documentFiles]: {
                value: data?.anexos || [],
                validations: [],
            },
        };
    };

    const fetchData = async () => {
        try {
            if (id) {
                const documentDetails = await documentsTrafficViolationsService.find(id);
                setAttachmentFileName(documentDetails.anexos || []);
                setFormData(initFormData(documentDetails));
            } else {
                setFormData(initFormData({}));
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const toFormData = () => {
        const formValues = {
            ait: parentId,
            tipo: formData[ids.documentType].value,
            data_documento: isoToBrFormat(formData[ids.documentDate].value),
            observacoes: formData[ids.documentObservations].value,
        };

        const data = new FormData();
        data.append("requestData", new Blob([JSON.stringify(formValues)], { type: "application/json" }));

        const files = attachmentRef?.current?.getFiles?.();
        if (files && Array.isArray(files)) {
            files.forEach((file) => {
                data.append("anexos", file);
            });
        }

        return data;
    };

    const handleSubmit = async (e) => {
        if (isFormInvalid) return;

        try {
            const data = toFormData();

            if (id) {
                await documentsTrafficViolationsService.update(data, id);
                showToast({ title: t("documents.messages.success.edit"), status: "success" });
            } else {
                await documentsTrafficViolationsService.create(data);
                showToast({ title: t("documents.messages.success.create"), status: "success" });
            }

            navigate(`${ROUTES.trafficViolationsDocumentsPath}/?parent_id=${parentId}`);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const baseFormData = { ...formData, [id]: { ...formData[id], value } };

        setFormData(baseFormData);
        setInputError(checkErrors({ formData: baseFormData, inputError, name: id }));
    };

    const handleDocumentDateChange = (value) => {
        const formatted =
            moment(value).isValid()
                ? moment(value).format(DATE_FORMAT_ISO)
                : "";

        const newFormData = {
            ...formData,
            [ids.documentDate]: {
                ...formData[ids.documentDate],
                value: formatted,
            },
        };

        setFormData(newFormData);

        setInputError(
            checkErrors({
                formData: newFormData,
                inputError,
                name: ids.documentDate,
            })
        );
    };

    const handleDownloadFile = async (fileName) => {
        try {
            await downloadFile(parentType, parentId, id, fileName)
        } catch (error) {
            showToast({
                title: t("system.messages.errors.attachment-download"),
                status: "error",
            });
        }
    };

    const handlePrintFile = async (fileName) => {
        try {
            await printFile(parentType, parentId, id, fileName)
        } catch (error) {
            showToast({
                title: t("system.messages.errors.print-unavailable"),
                status: "error",
            });
        }
    };

    const handleDeleteFile = async (fileName) => {
        try {
            await documentsService.deleteAttachment(parentType, parentId, id, fileName);
            fetchTrafficViolationData();
        } catch (error) {
            showToast({
                title: t("system.messages.errors.attachment-delete"),
                status: "error",
            });
        }
    }

    const fetchTrafficViolationData = async () => {
        if (!parentId) return;

        try {
            const data = await trafficViolationService.details(parentId);
            setTrafficViolationData(data);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        if (trafficViolationData) {
            fetchData();
        }
    }, [trafficViolationData, id]);

    useEffect(() => {
        fetchTrafficViolationData();
    }, []);

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>
                {trafficViolationData && (
                    <TrafficViolationDocumentsListPageCardHeader trafficViolationData={trafficViolationData} />
                )}
                <ListPageCardHeader title={t(id ? 'documents.form.edit-title' : 'documents.form.title')} />
                <CardBody px="22px">
                    {formData ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("documents.form.fields.type.label")}
                                    </FormLabel>
                                    <HStack align="center" justify="space-between">
                                        {!id ? (
                                            <SearchSelect
                                                required
                                                id={ids.documentType}
                                                value={formData[ids.documentType].value}
                                                items={trafficViolationDocumentTypes}
                                                valueField="value"
                                                displayField="label"
                                                placeholder={t('documents.form.fields.type.placeholder')}
                                                onChange={(e) => {
                                                    handleInputChange(e);
                                                    setInputError({});
                                                }}
                                            />
                                        ) : (
                                            <Input
                                                isDisabled
                                                fontSize="sm"
                                                variant="main"
                                                id={ids.documentType}
                                                value={
                                                    trafficViolationDocumentTypes.find((opt) => opt.value === formData[ids.documentType].value)?.label ||
                                                    formData[ids.documentType].value
                                                }
                                            />
                                        )}
                                    </HStack>
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentDate])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.documentDate]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.document-date.label")}
                                    </FormLabel>
                                    <DateTime
                                        id={ids.documentDate}
                                        locale={LOCALE_PT_BR}
                                        dateFormat={DATE_FORMAT}
                                        timeFormat={false}
                                        value={
                                            formData[ids.documentDate].value
                                                ? moment(formData[ids.documentDate].value, DATE_FORMAT_ISO)
                                                : ""
                                        }
                                        renderInput={(props) => (
                                            <Input
                                                {...props}
                                                fontSize="sm"
                                                variant="main"
                                                id={ids.documentDate}
                                            />
                                        )}
                                        onChange={handleDocumentDateChange}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentDate])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.documentObservations]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.observations.label")}
                                    </FormLabel>
                                    <Textarea
                                        minH="120px"
                                        bg={bgTextarea}
                                        id={ids.documentObservations}
                                        value={formData[ids.documentObservations].value}
                                        onChange={handleInputChange}
                                        _placeholder={{ color: placeholderColor }}
                                        placeholder={t('documents.form.fields.observations.placeholder')}
                                        fontSize="sm"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentObservations])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <FileAttachmentMultipleInput
                                ref={attachmentRef}
                                nameVariable={fileAttachmentName}
                                onDownloadFile={handleDownloadFile}
                                onPrintFile={handlePrintFile}
                                onDeleteFile={handleDeleteFile}
                                acceptedTypes={['application/pdf', 'image/jpeg']}
                            />

                            <Flex w="100%" justify="space-between">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={() => navigate(`${ROUTES.trafficViolationsDocumentsPath}/?parent_id=${parentId}`)}
                                >
                                    {t("system.actions.cancel")}
                                </Button>
                                <Button
                                    w="100px"
                                    variant="dark"
                                    fontSize="sm"
                                    onClick={handleSubmit}
                                    _hover={{
                                        bg: "grey.500",
                                        color: "white"
                                    }}
                                    isDisabled={isFormInvalid}
                                >
                                    {t(id ? 'system.actions.edit' : 'system.actions.save')}
                                </Button>
                            </Flex>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
};

export default TrafficViolationDocumentsNew;
